import CreateNewFolderOutlined from "@mui/icons-material/CreateNewFolderOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import DriveFileMoveOutlined from "@mui/icons-material/DriveFileMoveOutlined";
import DriveFileRenameOutline from "@mui/icons-material/DriveFileRenameOutline";
import LinkOutlined from "@mui/icons-material/LinkOutlined";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

type CustomTreeItemMenuProps = {
  menuOpen: boolean;
  menuAnchor: HTMLElement | null;
  onMenuOpen: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMenuClose: () => void;
  onRename: () => void;
  onGetSharingLink: () => string;
  onCreateSubFolder: () => void;
  onArchive: () => void;
};

export const CustomTreeItemMenu = ({
  menuOpen,
  menuAnchor,
  onMenuOpen,
  onMenuClose,
  onRename,
  onGetSharingLink,
  onCreateSubFolder,
  onArchive,
}: CustomTreeItemMenuProps): React.ReactElement => {
  const { t } = useTranslation();

  return (
    <>
      <IconButton
        size="small"
        className="folder-menu-button"
        onClick={onMenuOpen}
        aria-label={t("Folder actions")}
        data-analytics-id="content-library-folder-menu"
      >
        <MoreHoriz fontSize="small" />
      </IconButton>
      <Menu
        open={menuOpen}
        anchorEl={menuAnchor}
        onClose={onMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
      >
        <MenuItem
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onRename();
          }}
        >
          <ListItemIcon>
            <DriveFileRenameOutline fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("Rename")}</ListItemText>
        </MenuItem>
        <MenuItem onClick={onCreateSubFolder}>
          <ListItemIcon>
            <CreateNewFolderOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("Create sub-folder")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            const shareLink = onGetSharingLink();
            void navigator.clipboard?.writeText(shareLink);
            toast.success(t("Link copied to clipboard"));
            onMenuClose();
          }}
        >
          <ListItemIcon>
            <LinkOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("Get sharing link")}</ListItemText>
        </MenuItem>
        <MenuItem>
          <ListItemIcon>
            <DriveFileMoveOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("Move")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onArchive();
          }}
        >
          <ListItemIcon>
            <DeleteOutline fontSize="small" sx={{ color: "error.main" }} />
          </ListItemIcon>
          <ListItemText sx={{ color: "error.main" }}>{t("Archive")}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
